import React, { useState, useEffect, useMemo } from 'react';
import { ShoppingCart, PackageCheck, Zap } from 'lucide-react';
import { Product } from '@/context/types';
import useCatalog from '@/hooks/useCatalog';
import Page from '../container/Page';
import LoadingSpinner from '@/components/Loaders/PageLoader/loadingSpinner';
import {
  formatCurrency,
  formatCurrencyNumber,
} from '@/components/Input/helpers';
import HomeBox from './HomeBox';
import { CurveDivider } from '../container/PageHeader/PageHeader';
import DeliveryBtn from '../Order/DeliveryBtn/DeliveryBtn';
import { ORDER } from '@/constants';
import { useNavigate } from 'react-router-dom';
import HomeHeader from './HomeHeader';

// ============================================================================
// COMMERCIAL PATTERN: Generic Fisher-Yates Shuffle
// Declared outside the component so it doesn't need to be memoized.
// ============================================================================
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Home: React.FC = () => {
  // 1. Data Fetching
  const { hierarchy, isLoading, error } = useCatalog();

  // 2. Component State to hold the randomized products
  const [featuredBoxes, setFeaturedBoxes] = useState<Product[]>([]);
  const navigate = useNavigate();
  // ============================================================================
  // useEffect Logic
  // Handles the logic of filtering, shuffling, and selecting products ONLY once
  // when the component mounts or when the catalog data successfully loads.
  // ============================================================================
  useEffect(() => {
    if (isLoading || error || !hierarchy) return;

    // A. Gather all "Groupable" products (Pre-built Mix-and-Match Boxes)
    // We flatten the values from all mix types to get a complete list.
    const allBoxProducts = Object.values(
      hierarchy.mixAndMatchBoxes || {},
    ).flat();

    // B. Filter for unique products just in case they appear in multiple lists
    const uniqueBoxes = allBoxProducts.filter(
      (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i,
    );

    if (uniqueBoxes.length === 0) return;

    // C. Shuffle the complete list for true randomness
    const shuffledUniqueBoxes = shuffleArray(uniqueBoxes);

    // D. Pick a random number of products between 1 and 4
    const randomCount = uniqueBoxes.length < 4 ? uniqueBoxes.length : 4;

    // E. Select the first N products and commit them to state
    const selected = shuffledUniqueBoxes.slice(0, randomCount);
    setFeaturedBoxes(selected);
  }, [hierarchy, isLoading, error]); // Re-runs if the catalog changes or reloads

  // ============================================================================
  // Render States
  // ============================================================================

  // isLoading STATE
  if (isLoading) {
    return (
      <Page
        variant="transparent"
        mainClassName="!bg-brand-cream"
        pageHeader={<HomeHeader />}
      >
        <div className="flex flex-1 w-full h-screen items-center justify-center bg-brand-cream">
          <LoadingSpinner
            global
            message="Curating your hand-picked treats..."
          />
        </div>
      </Page>
    );
  }

  // ERROR OR NO DATA STATE
  if (error || (!isLoading && featuredBoxes.length === 0)) {
    return (
      <Page
        variant="transparent"
        mainClassName="!bg-brand-cream"
        pageHeader={<HomeHeader />}
      >
        <div className="flex flex-1 w-full h-screen items-center justify-center bg-brand-cream text-center p-8">
          <div className="p-12 bg-white rounded-[var(--radius-artisan)] shadow-lift border border-brand-truffle/5 max-w-lg">
            <Zap size={64} className="mx-auto text-brand-truffle/20 mb-8" />
            <h1 className="font-display font-bold text-4xl text-text-heading mb-4">
              A Quick Mix-Up!
            </h1>
            <p className="font-body text-text-main mb-8">
              We encountered a slight issue while gathering our best treats.
              Please try again in a moment, we're sure to have something
              delicious for you.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-3 rounded-full bg-brand-truffle text-brand-cream font-display font-bold text-sm tracking-widest uppercase"
            >
              Retry
            </button>
          </div>
        </div>
      </Page>
    );
  }

  // ============================================================================
  // Main Render
  // Maps over the randomized products and applies alternating flex-row logic.
  // ============================================================================
  return (
    <Page
      variant="primary"
      mainClassName="!bg-brand-cream"
      pageHeader={<HomeHeader />}
    >
      {/* PRODUCT BOXES SHOWCASE */}
      <section className="container mx-auto px-4 py-24 space-y-24 md:space-y-16">
        {featuredBoxes.map((box, index) => {
          // Determine if this is an even or odd block for layout switching
          const isEven = index % 2 === 0;

          return <HomeBox isEven={isEven} box={box} />;
        })}
      </section>

      {/* BOTTOM FINAL CALL TO ACTION */}
      <section className="bg-white border-t border-brand-truffle/5 py-20 px-4 text-center">
        <div className="container mx-auto max-w-xl">
          <h3 className="font-display font-bold text-3xl text-text-heading mb-6">
            Didn't find your perfect match?
          </h3>
          <p className="font-body text-text-main text-base mb-10 leading-relaxed">
            Our master confectioners are always mixing up new, artisan
            assortments. Click below to start building your own, completely
            custom flavor adventure from scratch.
          </p>
          {/* (Path 2) I did complete payment/checkout, delete entire cart from storage. */}
          <button
            onClick={() => navigate(ORDER)}
            className="flex items-center gap-2 px-10 py-4 mx-auto rounded-full bg-transparent text-brand-truffle border-2 border-brand-truffle font-display font-bold text-sm tracking-widest uppercase transition-all hover:bg-brand-truffle/5 hover:border-brand-truffle/20 lift-light"
          >
            Build a custom box
          </button>
        </div>
      </section>
    </Page>
  );
};

export default Home;
